import {useMemo, useRef, useState} from 'react';
import {Box, Button, Text} from '@chakra-ui/react';
import usePlannerStyles from '../planner/usePlannerStyles';
import {
  SearchSelectionInput,
  ViewportAwareDropdownMenu,
  handleDropdownInputKeyDown,
  handleDropdownOptionKeyDown,
} from '../planner/CreateSessionFormSections';
import {OTHER_REASON_CODE} from './telephoneEncounterReasons';
import type {ReasonPreset} from '../../types/telephoneEncounter';

const REASON_INPUT_SELECTOR = 'input[aria-label="Reason"]';

// The telephone-encounter reason picker. It reads as a normal text field that
// you just write the reason into — with a dropdown of the company presets to
// pick from as you type. Typing something that doesn't match a preset is fully
// supported: Enter, clicking the "Use …" option, or clicking away all commit
// the typed text as the reason. Custom (non-preset) reasons carry the `OTHER`
// code so downstream knows they're free text, but the field always shows the
// actual reason text — there's no separate "Other" mode to fall into.
const ReasonField = ({
  value,
  reasonCode,
  presets,
  onChange,
  onBlur,
  isDisabled = false,
}: {
  value: string;
  reasonCode: string | null;
  presets: ReasonPreset[];
  onChange: (reasonText: string, reasonCode: string | null) => void;
  onBlur?: () => void;
  isDisabled?: boolean;
}) => {
  const {
    borderColor,
    subtleText,
    surfaceBg,
    textColor,
    typeaheadHoverBg,
    typeaheadSelectedBg,
    typeaheadSelectedBorder,
  } = usePlannerStyles();

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  // The presets the user can pick from — the `OTHER` sentinel is an internal
  // code for free text, not a selectable row, so it's never shown.
  const selectablePresets = useMemo(
    () => presets.filter(preset => preset.code !== OTHER_REASON_CODE),
    [presets],
  );

  // What the field shows when closed: a picked preset's label, or the raw
  // free-text reason for a custom one.
  const committedReason = useMemo(() => {
    if (reasonCode && reasonCode !== OTHER_REASON_CODE) {
      const preset = selectablePresets.find(p => p.code === reasonCode);
      if (preset) return preset.label;
    }
    return value || '';
  }, [reasonCode, selectablePresets, value]);

  const filtered = useMemo(() => {
    const queryText = search.trim().toLowerCase();
    if (!queryText) return selectablePresets;
    return selectablePresets.filter(preset =>
      preset.label.toLowerCase().includes(queryText),
    );
  }, [selectablePresets, search]);

  // While open the input is the live editor (shows what's being typed); when
  // closed it shows the committed reason so it reads as a filled-in field.
  const displayValue = isOpen ? search : committedReason;

  // Open the menu, seeding the editor with the current reason so the user can
  // refine it inline instead of starting from a blank field.
  const openMenu = () => {
    if (isDisabled || isOpen) return;
    setSearch(committedReason);
    setIsOpen(true);
  };

  const handleSelect = (preset: ReasonPreset) => {
    onChange(preset.label, preset.code);
    setSearch('');
    setIsOpen(false);
  };

  // Commit whatever the registrar has typed. An exact (case-insensitive) match
  // resolves to that preset; anything else becomes a custom free-text reason
  // (code `OTHER`). Used by Enter, the "Use …" option, and blur so the user
  // never has to pick from the list to set a reason.
  const commitTyped = () => {
    const queryText = search.trim();
    if (!queryText) {
      setIsOpen(false);
      return;
    }
    const exact = selectablePresets.find(
      preset => preset.label.toLowerCase() === queryText.toLowerCase(),
    );
    if (exact) {
      onChange(exact.label, exact.code);
    } else {
      onChange(queryText, OTHER_REASON_CODE);
    }
    setSearch('');
    setIsOpen(false);
  };

  return (
    <Box
      ref={anchorRef}
      position="relative"
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          // Clicking away with typed text commits it (matching preset or custom)
          // rather than discarding it. `commitTyped` saves via onChange, so we
          // skip the parent onBlur in that path to avoid clobbering it with a
          // stale value; with nothing typed we fall back to the parent onBlur.
          if (search.trim()) {
            commitTyped();
          } else {
            setIsOpen(false);
            setSearch('');
            onBlur?.();
          }
        }
      }}
    >
      <SearchSelectionInput
        ariaControls="te-reason-results"
        ariaLabel="Reason"
        isOpen={isOpen}
        value={displayValue}
        placeholder="Type or select a reason"
        onFocus={openMenu}
        onClick={openMenu}
        onChange={(event: any) => {
          if (isDisabled) return;
          setSearch(event.target.value);
          setIsOpen(true);
        }}
        onKeyDown={(event: any) => {
          if (isDisabled) return;
          if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            commitTyped();
            return;
          }
          handleDropdownInputKeyDown({
            event,
            menuId: 'te-reason-results',
            onOpen: () => setIsOpen(true),
          });
        }}
      />

      {isOpen && !isDisabled && (
        <ViewportAwareDropdownMenu
          anchorRef={anchorRef}
          id="te-reason-results"
          className="menu"
          zIndex={1400}
          bg={surfaceBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          boxShadow="lg"
          maxHeightPx={280}
          role="listbox"
          aria-label="Reason results"
          p={2}
        >
          {filtered.length > 0 ? (
            filtered.map(preset => {
              const isSelected = preset.code === reasonCode;
              return (
                <Button
                  key={preset.code}
                  type="button"
                  className="item"
                  role="option"
                  aria-selected={isSelected}
                  aria-label={`Select ${preset.label}`}
                  variant="ghost"
                  justifyContent="flex-start"
                  w="100%"
                  h="auto"
                  minH="44px"
                  py={2.5}
                  px={3}
                  borderRadius="lg"
                  bg={isSelected ? typeaheadSelectedBg : 'transparent'}
                  borderWidth="1px"
                  borderColor={
                    isSelected ? typeaheadSelectedBorder : 'transparent'
                  }
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => handleSelect(preset)}
                  onKeyDown={event =>
                    handleDropdownOptionKeyDown(event, REASON_INPUT_SELECTOR)
                  }
                  _hover={{
                    bg: isSelected ? typeaheadSelectedBg : typeaheadHoverBg,
                  }}
                  _focus={{
                    bg: isSelected ? typeaheadSelectedBg : typeaheadHoverBg,
                    boxShadow: 'none',
                    outline: 'none',
                  }}
                  _focusVisible={{
                    bg: isSelected ? typeaheadSelectedBg : typeaheadHoverBg,
                    boxShadow: 'none',
                    outline: 'none',
                  }}
                >
                  <Text
                    color={textColor}
                    fontWeight="normal"
                    fontSize="md"
                    noOfLines={1}
                  >
                    {preset.label}
                  </Text>
                </Button>
              );
            })
          ) : search.trim() ? (
            <Button
              type="button"
              className="item"
              role="option"
              aria-selected={false}
              aria-label={`Use "${search.trim()}" as the reason`}
              variant="ghost"
              justifyContent="flex-start"
              w="100%"
              h="auto"
              minH="44px"
              py={2.5}
              px={3}
              borderRadius="lg"
              bg="transparent"
              borderWidth="1px"
              borderColor="transparent"
              onMouseDown={event => event.preventDefault()}
              onClick={commitTyped}
              onKeyDown={event =>
                handleDropdownOptionKeyDown(event, REASON_INPUT_SELECTOR)
              }
              _hover={{bg: typeaheadHoverBg}}
              _focus={{
                bg: typeaheadHoverBg,
                boxShadow: 'none',
                outline: 'none',
              }}
              _focusVisible={{
                bg: typeaheadHoverBg,
                boxShadow: 'none',
                outline: 'none',
              }}
            >
              <Text
                color={textColor}
                fontWeight="normal"
                fontSize="md"
                noOfLines={1}
              >
                Use “{search.trim()}”
              </Text>
            </Button>
          ) : (
            <Text color={subtleText} fontSize="sm" px={3} py={3}>
              No matching reasons.
            </Text>
          )}
        </ViewportAwareDropdownMenu>
      )}
    </Box>
  );
};

export default ReasonField;
